import {
  AzureOnboardingScopeRef,
  AzureScope,
} from '../../../services/azure/azureOnboarding/azureOnboarding.model';

export const statusDisplay: {
  [key: string]: { label: string; color: string; tooltip: string };
} = {
  AwaitingConsent: {
    label: 'Awaiting client consent',
    color: 'default',
    tooltip: "The client hasn't opened the link and granted consent yet.",
  },
  PendingRoleAssignment: {
    label: 'Waiting for Azure access',
    color: 'gold',
    tooltip:
      'Consent granted, but no active billing account or subscription yet. Either none is visible, or all are switched off.',
  },
  PendingPropagation: {
    label: 'Finishing setup',
    color: 'processing',
    tooltip:
      "Scopes are known, but none has answered yet. Roles may still be propagating, may be missing, or (for CSP) the partner hasn't turned on cost visibility.",
  },
  Connected: {
    label: 'Connected',
    color: 'success',
    tooltip: 'Data collection is running.',
  },
  Error: {
    label: 'Error',
    color: 'error',
    tooltip: 'Verification failed unexpectedly - see the server log.',
  },
};

export const getStatusDisplay = (status: string) =>
  statusDisplay[status] || { label: status, color: 'default', tooltip: '' };

export const scopeStatusColor: { [key: string]: string } = {
  Connected: 'success',
  Pending: 'processing',
  Failed: 'error',
  NotTested: 'default',
};

// scope_type is the ARM path segment in front of the scope id ('subscriptions',
// 'providers/Microsoft.Billing/billingAccounts', or any segment an admin adds). Like the
// backend's isScopeType, compare case-insensitively and ignore surrounding slashes. The
// pre-migration names ('Subscription', 'BillingAccount') are accepted too.
const normalizeScopeType = (type: string | null | undefined) =>
  (type ?? '').trim().replace(/^\/+|\/+$/g, '').toLowerCase();

const SUBSCRIPTION_TYPES = ['subscriptions', 'subscription'];
const BILLING_ACCOUNT_TYPES = ['providers/microsoft.billing/billingaccounts', 'billingaccount'];

export const isSubscription = (scope: { scope_type: string }) =>
  SUBSCRIPTION_TYPES.includes(normalizeScopeType(scope.scope_type));

export const isBillingAccount = (scope: { scope_type: string }) =>
  BILLING_ACCOUNT_TYPES.includes(normalizeScopeType(scope.scope_type));

// Matches the backend: CSP = a Subscription whose quotaId starts with "CSP".
export const isCspScope = (scope: { scope_type: string; agreement_type?: string | null }) =>
  isSubscription(scope) && (scope.agreement_type ?? '').toUpperCase().startsWith('CSP');

export const scopeTypeLabel = (scopeType: string) => {
  if (isSubscription({ scope_type: scopeType })) return 'Subscription';
  if (isBillingAccount({ scope_type: scopeType })) return 'Billing account';
  return scopeType;
};

export const agreementLabel = (agreementType: string | null) => {
  switch (agreementType) {
    case 'MicrosoftCustomerAgreement':
      return 'MCA';
    case 'EnterpriseAgreement':
      return 'EA';
    default:
      return agreementType ?? '-';
  }
};

export const clientLink = (linkToken: string) =>
  `${window.location.origin}/connect-azure/${linkToken}`;

export const scopeName = (ref: AzureOnboardingScopeRef, scopes: AzureScope[]) =>
  scopes.find((s) => s.id === ref.id)?.display_name || ref.scope_id;

export const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '–';

// The request interceptor already toasts these; this is for showing them inline as well.
export const responseErrorText = (err: any): string => {
  const errors = err?.response?.data?.body?.errors;
  if (Array.isArray(errors)) return errors.join(' ');
  return errors || 'The request failed.';
};

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for browsers that block clipboard API
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
};
